import { z } from "zod/v4";

export const ALL_USERS = "AllUsers";
export const LOGGED_IN_USERS = "LoggedInUsers";

export interface Email {
	type: "Email";
	value: string;
}

export function isEmail(o: unknown): o is Email {
	return !!o && typeof o === "object" && "type" in o && o.type === "Email";
}

export type PermRelation = "reader" | "collaborator" | "owner";
export type NonOwnerPermRelation = Omit<PermRelation, "owner">;

export type DocPermission = "read" | "write" | "share" | "own";
export type TablePermission = "read_all_rows" | "read_self_rows" | "write_rows";

export interface DocChangePermRequest {
	type: typeof ALL_USERS | typeof LOGGED_IN_USERS | "Username" | "Email" | "Workspace";
	relation: PermRelation;
	value?: string;
}

export interface ShareType {
	// are we sharing publically or with a specific user?
	type: "grant" | "revoke";
	userType: "Username" | "Email";
	relation: NonOwnerPermRelation;
	user?: SearchUserResult;
	inviteEmail?: string;
}

export interface UpdateUserPermission {
	user: SearchUserResult;
	type: "grant" | "revoke";
	relation: PermRelation;
}

export interface UpdateWorkspacePermission {
	type: "grant" | "revoke";
	relation: PermRelation;
}

export interface InvitationUpdate {
	email: string;
	type: "grant" | "revoke";
	relation: PermRelation;
}

// These are users fetching from the sharing modal
// We do not send email for now as that is PII

/*
 [
  {
    "name": "Alex Clemmer",
    "nickname": "hausdorff",
    "picture": "https://avatars.githubusercontent.com/u/1409156?v=4",
    "user_id": "github|1409156"
  }
]
*/
const SearchUserResultSchema = z.object({
	name: z.string(),
	nickname: z.string(),
	picture: z.string().url(),
	user_id: z.string(),
});

export type SearchUserResult = z.infer<typeof SearchUserResultSchema>;

const UserWithPermissionSchema = SearchUserResultSchema.extend({
	email: z.string().regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/),
	permission: z.enum(["read", "write", "own"]),
});

export type UserWithPermission = z.infer<typeof UserWithPermissionSchema>;

export const InviteResultSchema = z.object({
	email: z.string().regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/),
	referrer: z.string(),
	relation: z.enum(["reader", "collaborator", "owner"]),
});

export type Invitation = z.infer<typeof InviteResultSchema>;

const PermissionInfoSchema = z.object({
	permission: z.enum(["read", "write", "share", "own"]),
	users: z.array(SearchUserResultSchema),
	appliedToEveryoneInWorkspace: z.boolean(),
});

export type PermissionInfo = z.infer<typeof PermissionInfoSchema>;
